import { useMemo, useState } from 'react'
import { Controller, type UseFormReturn } from 'react-hook-form'
import { isFieldVisible, type DocumentDef, type DocumentStatus, type LineColumnDef } from '@project/contracts'
import { Button, Panel } from '../design/index.js'
import { useApi, type LineRow } from '../app/api.js'
import { FormField } from './controls.js'
import { asText } from './format.js'
import { LineGrid, type PickableRow } from './LineGrid.js'
import { LineSources } from './LineSources.js'
import { useSettings } from './queries.js'

export type DocumentForm = UseFormReturn<Record<string, unknown>>

export interface FormTabProps {
  readonly def: DocumentDef
  readonly form: DocumentForm
  readonly status: DocumentStatus
  readonly lines: readonly LineRow[]
  readonly fieldLabels: Readonly<Record<string, string>>
  readonly documentId: string
  readonly onLinesChange: (lines: readonly LineRow[]) => void
  readonly readOnly: boolean
}

/**
 * The label a picker shows for one row of another grid.
 *
 * A BOQ line reads as its reference and description; a billing term as its
 * name and the line under it. Falls back to the row number, so a row that has
 * been added and not yet filled in is still choosable.
 */
const rowLabel = (row: LineRow, index: number): string => {
  const parts = [
    asText(row['boqCode']) || asText(row['termName']),
    asText(row['itemLabel']) || asText(row['itemName']) || asText(row['description']),
  ].filter((p) => p !== '')
  return parts.length > 0 ? parts.join(' — ') : `Row ${index + 1}`
}

/**
 * The rows a picker column may choose from.
 *
 * A saved row is chosen by its id. One added in this same edit has no id yet,
 * so it is chosen by the `seq:<SECTION>:<n>` key the save path resolves to a
 * real id once both rows exist — which is what lets a BOQ line and the estimate
 * under it be typed in one sitting.
 */
const pickableFrom = (lines: readonly LineRow[], section: string): PickableRow[] =>
  lines
    .filter((l) => String(l['section'] ?? '') === section)
    .map((l, index) => ({
      value: asText(l['id']) || `seq:${section}:${asText(l['seq']) || index + 1}`,
      label: rowLabel(l, index),
    }))

/**
 * The whole editable document, rendered from `def.form.sections`, `def.lines`
 * and `def.grids`. Section numbers come from the definition so the screen and
 * the printed PDF are numbered identically.
 *
 * Every grid the definition declares is drawn. They were not, and the ones that
 * were missing were the ones the product is about: a project's estimate and its
 * billing terms, the suppliers invited to an RFQ, and the additional charges and
 * deductions on nine document types. All of it was stored, computed and
 * validated by the server, and none of it could be entered.
 */
export const FormTab = ({ def, form, status, lines, fieldLabels, onLinesChange, readOnly, documentId }: FormTabProps) => {
  const leftDraft = status !== 'DRAFT'
  const settings = useSettings()
  const values = settings.data?.values

  const primarySection = def.lines?.section ?? 'ITEM'

  /** One grid's rows. Everything is one list; `section` is what separates them. */
  const rowsOf = (section: string): LineRow[] =>
    lines.filter((l) => String(l['section'] ?? primarySection) === section)

  /**
   * Puts one grid's rows back, leaving every other grid's alone.
   *
   * The document is saved as a single list of lines, so editing one grid has to
   * rewrite only its own slice — otherwise adding a charge would delete the
   * items beside it.
   */
  const replaceSection = (section: string, next: readonly LineRow[]): void => {
    const others = lines.filter((l) => String(l['section'] ?? primarySection) !== section)
    onLinesChange([...others, ...next.map((l) => ({ ...l, section }))])
  }

  /** What each of a grid's picker columns may choose from. */
  const pickableFor = (grid: { columns: readonly LineColumnDef[]; parentSection?: string }) => {
    const out: Record<string, PickableRow[]> = {}
    for (const column of grid.columns) {
      if (!column.parentPicker) continue
      const from = column.picksFrom ?? grid.parentSection
      if (from) out[column.key] = pickableFrom(lines, from)
    }
    return out
  }

  // A company that does not use line discounts should not be shown the column;
  // the server ignores what is typed into it either way.
  const lineColumns = useMemo(() => {
    const all = def.lines?.columns ?? []
    return values?.['line_discount_enabled'] === false
      ? all.filter((c) => c.key !== 'discountPct')
      : all
  }, [def.lines, values])

  const overallDiscount = values?.['overall_discount_enabled'] === true

  // What has been answered so far, so a question that depends on another
  // appears and disappears as it is answered.
  const watched = form.watch()

  /*
    Only the questions that are actually being asked.

    `visibleWhen` and `requiresSetting` were authored on the definitions and
    read by nobody on this side, so a stock purchase order still demanded a
    project, and the e-invoice and e-way-bill fields sat on every invoice and
    challan whether or not the company had those integrations switched on. A
    hidden field is not required either — `required` on a question nobody is
    asked is a form that cannot be submitted.
  */
  const visibleFields = (fields: DocumentDef['form']['sections'][number]['fields']) =>
    fields.filter((field) => isFieldVisible(field, watched, values))

  return (
    <>
      {def.form.sections.map((section) => (
        <Panel key={section.key} n={section.n} title={section.title}>
          <div className="field-grid">
            {visibleFields(section.fields).map((field) => (
              <Controller
                key={field.key}
                name={field.key}
                control={form.control}
                rules={{ required: field.required === true ? `${field.label} is required.` : false }}
                render={({ field: bound, fieldState }) => (
                  <FormField
                    field={field}
                    value={bound.value ?? ''}
                    valueLabel={fieldLabels[field.key]}
                    onChange={bound.onChange}
                    disabled={readOnly || (field.lockAfterDraft === true && leftDraft)}
                    error={fieldState.error?.message}
                  />
                )}
              />
            ))}
          </div>
        </Panel>
      ))}

      {def.lines ? (
        <Panel
          n={def.lines.n}
          title={def.lines.title}
          aside={`${lines.length} line${lines.length === 1 ? '' : 's'}`}
        >
          {!readOnly && documentId ? (
            <LineSources
              docKey={def.key}
              documentId={documentId}
              sources={def.lines.sources}
              lines={lines}
              onAdd={onLinesChange}
            />
          ) : null}
          {!readOnly && def.allocatesFrom?.length && documentId ? (
            <PullSourceLines
              docKey={def.key}
              documentId={documentId}
              label={def.lines.sources.find((x) => x.source === 'order')?.label ?? 'Pull the open lines'}
              existing={lines}
              onPulled={onLinesChange}
            />
          ) : null}
          <LineGrid
            columns={lineColumns}
            lines={rowsOf(primarySection)}
            readOnly={readOnly}
            onChange={(next) => replaceSection(primarySection, next)}
            addLabel={def.lines.addLabel}
            section={primarySection}
            pickable={pickableFor(def.lines)}
          />
          {overallDiscount ? (
            <div className="overall-discount">
              <label className="field__label" htmlFor="overall-discount">Discount on the whole document (%)</label>
              <input
                id="overall-discount"
                className="control"
                style={{ width: 140 }}
                inputMode="decimal"
                disabled={readOnly}
                value={String(form.watch('overallDiscountPct') ?? '')}
                onChange={(e) => form.setValue('overallDiscountPct', e.currentTarget.value, { shouldDirty: true })}
              />
              <p className="field__hint">
                Spread across the lines, so tax is charged on the discounted value of each.
              </p>
            </div>
          ) : null}
        </Panel>
      ) : null}

      {(def.grids ?? []).map((grid) => {
        const section = grid.section ?? 'ITEM'
        const rows = rowsOf(section)
        return (
          <Panel
            key={section}
            n={grid.n}
            title={grid.title}
            aside={`${rows.length} line${rows.length === 1 ? '' : 's'}`}
          >
            {!readOnly && documentId && grid.sources.length > 0 ? (
              <LineSources
                docKey={def.key}
                documentId={documentId}
                sources={grid.sources}
                lines={lines}
                onAdd={onLinesChange}
              />
            ) : null}
            <LineGrid
              columns={grid.columns}
              lines={rows}
              readOnly={readOnly}
              onChange={(next) => replaceSection(section, next)}
              addLabel={grid.addLabel}
              section={section}
              pickable={pickableFor(grid)}
            />
          </Panel>
        )
      })}
    </>
  )
}

/**
 * Pulls the parent document's still-open lines into this one.
 *
 * This is the only way an allocation gets created from the interface. Without
 * it the ledger — the thing the whole architecture is built around — was
 * reachable only through the API, and "Ordered" and "Still open" were blank on
 * every line a person typed.
 */
export const PullSourceLines = ({
  docKey, documentId, label, existing, onPulled,
}: {
  docKey: string
  documentId: string
  label: string
  existing: readonly LineRow[]
  onPulled: (lines: LineRow[]) => void
}) => {
  const api = useApi()
  const [busy, setBusy] = useState(false)
  const [note, setNote] = useState<string | null>(null)

  const pull = async (): Promise<void> => {
    setBusy(true)
    setNote(null)
    try {
      const source = await api.sourceLines(docKey, documentId)
      const already = new Set(existing.map((l) => String(l['fromLineId'] ?? '')))
      const open = source.filter((l) => Number(l.openQty) > 0 && !already.has(l.fromLineId))
      if (open.length === 0) {
        setNote(
          source.length === 0
            ? 'There is no order on this document to pull from yet.'
            : 'Every line on that order is already accounted for.',
        )
        return
      }
      onPulled([
        ...existing,
        ...open.map((l, i) => ({
          seq: existing.length + i + 1,
          fromLineId: l.fromLineId,
          itemId: l.itemId,
          itemLabel: l.itemLabel,
          description: l.description,
          brand: l.brand,
          unit: l.unit,
          rate: l.rate,
          orderedQty: l.orderedQty,
          openQty: l.openQty,
          // Travels with the line so a challan raised from a receipt can be
          // split into what the customer is charged for and what merely has to
          // reach the site.
          isBillable: l.isBillable ? 'YES' : 'NO',
          /*
            What the order actually agreed, not what the item master says.

            Only the item, description, brand, unit, rate and quantities used
            to be copied, so a purchase invoice pulled from an order silently
            lost a line-level HSN override and any discount negotiated on the
            order — silently, because `hsn_missing` falls back to the master
            and blocks nothing.
          */
          ...(l.hsnSacCode ? { hsnSacCode: l.hsnSacCode } : {}),
          ...(l.taxPct !== null && l.taxPct !== undefined ? { taxPct: l.taxPct } : {}),
          ...(l.discountPct !== null && l.discountPct !== undefined ? { discountPct: l.discountPct } : {}),
          ...(l.billingTermLineId ? { billingTermLineId: l.billingTermLineId } : {}),
          // Pre-filled with what is still open; the operator edits it down to
          // what actually turned up. Where a receipt is named, what turned up
          // is already known, so that is the better suggestion.
          quantity: l.receivedQty ?? l.suggestedQty ?? l.openQty,
          // Whatever else the source says the row starts with (docs/24).
          ...(l.carry ?? {}),
        })),
      ])
      setNote(
        `${open.length} line${open.length === 1 ? '' : 's'} pulled in. `
        + (open.some((l) => l.receivedQty !== undefined)
          ? 'The quantity is what the receipt took in.'
          : 'Change the quantity to what actually arrived.'),
      )
    } catch (err) {
      setNote(err instanceof Error ? err.message : 'Could not read the order.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div style={{ marginBottom: 16 }}>
      <Button kind="ghost" onClick={() => { void pull() }} disabled={busy}>
        {busy ? 'Reading the order…' : label}
      </Button>
      {note ? (
        <div style={{ font: '400 14px/1.6 var(--font-body)', color: 'var(--muted)', marginTop: 10 }}>{note}</div>
      ) : null}
    </div>
  )
}
